import { useCallback, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useIntl } from 'react-intl';

import {
  getListAvailableUserProfilesQueryKey,
  useListAvailableUserProfiles,
  UserProfileDTO,
} from '@/api/auth';
import { useImpersonateOrganizationAdmin } from '@/api/trafikportalen';
import { useConfig, useRoleIds } from '@/contexts/ConfigContext';
import { useAuth } from '@/stores/auth';

import { toast } from './useToast';

interface UseImpersonationOptions {
  profiles?: UserProfileDTO[];
}

export const useImpersonation = (options?: UseImpersonationOptions) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const { setUserProfile, setPreImpersonationLocation } = useAuth();
  const navigate = useNavigate();
  const roleIds = useRoleIds();
  const config = useConfig();
  const impersonateOrganizationAdmin = useImpersonateOrganizationAdmin();
  const { data: fetchedProfiles } = useListAvailableUserProfiles(
    {
      application_uri: config.auth.applicationUri,
    },
    {
      query: {
        enabled: !options?.profiles,
      },
    },
  );

  const profiles = options?.profiles ?? fetchedProfiles;

  const [temporaryId, setTemporaryId] = useState<string | null>(null);
  const [pendingRoute, setPendingRoute] = useState<string>('');
  const [pendingParams, setPendingParams] = useState<Record<string, string>>(
    {},
  );

  const switchProfile = useCallback(
    async (
      profile: UserProfileDTO,
      route: string,
      params: Record<string, string>,
    ) => {
      // Keep these back to back: a render in between would refetch the
      // cleared queries with the old profile.
      queryClient.clear();
      setUserProfile(profile);

      await navigate({
        to: route,
        params: {
          ...params,
          tenantId: profile.tenant_id,
        },
      });
    },
    [queryClient, setUserProfile, navigate],
  );

  // Must wait for the profile to be created and fetched
  useEffect(() => {
    if (!temporaryId || !profiles) {
      return;
    }

    const profile = profiles.find((profile) => profile.id === temporaryId);

    if (!profile) {
      return;
    }

    setTemporaryId(null);
    switchProfile(profile, pendingRoute, pendingParams);
  }, [temporaryId, profiles, switchProfile, pendingRoute, pendingParams]);

  return useCallback(
    (
      organizationId: string,
      targetRoute?: string,
      targetParams: Record<string, string> = {},
    ) => {
      const route = targetRoute || '/$tenantId';

      if (!useAuth.getState().userProfile?.expires_at) {
        setPreImpersonationLocation(
          window.location.pathname +
            window.location.search +
            window.location.hash,
        );
      }

      const hasExistingProfile = profiles?.find(
        (profile) =>
          profile.organization_id === organizationId &&
          profile.role_id === roleIds.orgAdminWrite,
      );

      if (hasExistingProfile) {
        if (!hasExistingProfile.expires_at) {
          toast.info({
            title: formatMessage({
              defaultMessage: 'Använder existerande profil',
            }),
          });
        }
        switchProfile(hasExistingProfile, route, targetParams);
        return;
      }

      setPendingRoute(route);
      setPendingParams(targetParams);

      impersonateOrganizationAdmin.mutate(
        {
          organizationID: organizationId,
        },
        {
          async onSuccess(data) {
            setTemporaryId(data.user_profile_id);
            await queryClient.invalidateQueries({
              queryKey: getListAvailableUserProfilesQueryKey(),
            });
          },
        },
      );
    },
    [
      profiles,
      impersonateOrganizationAdmin,
      formatMessage,
      switchProfile,
      queryClient,
      roleIds,
      setPreImpersonationLocation,
    ],
  );
};
